'use memo';

import {
  Condition,
  ConditionType,
  CoolingOffDetails,
  CoolingOffType,
  DayType,
  Deposit,
  DepositType,
  FormCondition,
  NoCoolingOffReason,
  StaffGetContractDatesResponse,
  StaffGetContractDepositsResponse,
  StaffGetContractSettlementResponse,
  StaffGetSaleInformationResponse,
  StaffGetSaleInformationResponseUnconditionalDisplayAs,
  StaffGetSaleInformationResponseUnderOfferDisplayAs,
  StaffUpdateContractDatesRequest,
  StaffUpdateContractDepositsRequest,
  StaffUpdateContractSettlementRequest,
  StaffUpdateSaleInformationRequest,
  StaffUpdateSaleInformationRequestUnconditionalDisplayAs,
  StaffUpdateSaleInformationRequestUnderOfferDisplayAs,
  TimeUnit,
  useInvalidateUseStaffGetContractDates,
  useInvalidateUseStaffGetContractDeposits,
  useInvalidateUseStaffGetContractSettlement,
  useInvalidateUseStaffGetSaleInformation,
  useStaffGetContractDates,
  useStaffGetContractDeposits,
  useStaffGetContractSettlement,
  useStaffGetSaleInformation,
  useStaffUpdateContractDates,
  useStaffUpdateContractDeposits,
  useStaffUpdateContractSettlement,
  useStaffUpdateSaleInformation,
  ValueType,
} from '@urbanx/agx.campaigns.client';
import { useIsFetching, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { TaskCategoryTaskType } from '@urbanx/agx.activities.client';
import { dateOnlyToDate, toDateOnlyString } from '@urbanx/agx.ui-components2';
import {
  isExchangeContractDatesTaskType,
  isExchangeInformationTaskType,
} from 'utils/tasks/exchangeTaskIdentity';
import { buildExchangeDeposits, DepositPayableOption } from './exchangeTaskDeposits';

const toContractDateRequest = (value?: Date): StaffUpdateContractDatesRequest['contractDate'] =>
  toDateOnlyString(value) as StaffUpdateContractDatesRequest['contractDate'];

export interface ExchangeTaskFormState {
  contractDate?: Date;
  salePrice?: number;
  deposit?: Deposit;
  depositPayableOption?: DepositPayableOption;
  agencyAdminNoted?: boolean;
  coolingOffType?: CoolingOffType;
  customCoolingOffDays?: number;
  noCoolingOffReason?: NoCoolingOffReason;
  subjectToFinance?: FormCondition;
  settlementInfo?: FormCondition;
}

const createInitialExchangeTaskFormState = (): ExchangeTaskFormState => ({
  settlementInfo: {
    condition: Condition.NdaysFromDate,
    fromDateTimeUnit: TimeUnit.Days,
  },
});

const buildCoolingOffDetails = (state: ExchangeTaskFormState): CoolingOffDetails | undefined => {
  if (!state.coolingOffType) return undefined;

  return {
    coolingOffType: state.coolingOffType,
    coolingOffDays:
      state.coolingOffType === CoolingOffType.NDays ? state.customCoolingOffDays : undefined,
    noCoolingOffReason:
      state.coolingOffType === CoolingOffType.NoCoolingOff ? state.noCoolingOffReason : undefined,
  };
};

const buildSubjectToFinance = (state: ExchangeTaskFormState): FormCondition | undefined => {
  if (!state.subjectToFinance?.condition) return undefined;

  return state.subjectToFinance;
};

export const buildExchangeDatesRequest = (
  state: ExchangeTaskFormState,
  contractDates: StaffGetContractDatesResponse | undefined
): StaffUpdateContractDatesRequest => ({
  contractDate: toContractDateRequest(state.contractDate),
  coolingOffDetails: buildCoolingOffDetails(state),
  waiverOfCoolingOff: contractDates?.waiverOfCoolingOff,
  subjectToFinance: buildSubjectToFinance(state),
  buildingAndPestCondition: contractDates?.buildingAndPestCondition,
  bodyCorporateRecordsCondition: contractDates?.bodyCorporateRecordsCondition,
});

const toRequestUnderOfferDisplayAs = (
  value: StaffGetSaleInformationResponseUnderOfferDisplayAs | undefined
): StaffUpdateSaleInformationRequestUnderOfferDisplayAs | undefined =>
  value === StaffGetSaleInformationResponseUnderOfferDisplayAs.UnderOffer
    ? StaffUpdateSaleInformationRequestUnderOfferDisplayAs.UnderOffer
    : value === StaffGetSaleInformationResponseUnderOfferDisplayAs.Available
      ? StaffUpdateSaleInformationRequestUnderOfferDisplayAs.Available
      : undefined;

const toRequestUnconditionalDisplayAs = (
  value: StaffGetSaleInformationResponseUnconditionalDisplayAs | undefined
): StaffUpdateSaleInformationRequestUnconditionalDisplayAs | undefined =>
  value === StaffGetSaleInformationResponseUnconditionalDisplayAs.UnderOffer
    ? StaffUpdateSaleInformationRequestUnconditionalDisplayAs.UnderOffer
    : value === StaffGetSaleInformationResponseUnconditionalDisplayAs.Sold
      ? StaffUpdateSaleInformationRequestUnconditionalDisplayAs.Sold
      : value === StaffGetSaleInformationResponseUnconditionalDisplayAs.DoNotUpdate
        ? StaffUpdateSaleInformationRequestUnconditionalDisplayAs.DoNotUpdate
        : undefined;

export const buildExchangeInformationRequests = (
  state: ExchangeTaskFormState,
  saleInformation: StaffGetSaleInformationResponse | undefined
) => ({
  saleRequest: {
    salePrice: state.salePrice,
    displaySalePrice: saleInformation?.displaySalePrice ?? false,
    underOfferDisplayAs: toRequestUnderOfferDisplayAs(saleInformation?.underOfferDisplayAs),
    unconditionalDisplayAs: toRequestUnconditionalDisplayAs(
      saleInformation?.unconditionalDisplayAs
    ),
  } satisfies StaffUpdateSaleInformationRequest,
  depositsRequest: {
    deposits: buildExchangeDeposits(state),
  } satisfies StaffUpdateContractDepositsRequest,
});

const assertSuccessfulUpdate = (response: { success?: boolean; errors?: string[] } | undefined) => {
  if (response?.success) return;

  throw new Error(response?.errors?.join(', ') || 'Failed to save contract changes');
};

export const buildSettlementInfo = (state: ExchangeTaskFormState): FormCondition | undefined => {
  const settlementInfo = state.settlementInfo;

  if (settlementInfo?.condition === Condition.OnSpecificDate) {
    if (!settlementInfo.referenceDate) return undefined;

    return {
      ...settlementInfo,
      condition: Condition.OnSpecificDate,
      conditionType: ConditionType.Contract,
    };
  }

  if (settlementInfo?.condition !== Condition.NdaysFromDate || !settlementInfo.daysFromDate) {
    return undefined;
  }

  return {
    ...settlementInfo,
    condition: Condition.NdaysFromDate,
    conditionType: ConditionType.Contract,
    dayType: DayType.Days,
    fromDateTimeUnit: settlementInfo.fromDateTimeUnit ?? TimeUnit.Days,
  };
};

export const useExchangeTaskForm = (
  campaignId: string | undefined,
  contractId: string | undefined,
  enabled: boolean
) => {
  const [state, setState] = useState<ExchangeTaskFormState>(createInitialExchangeTaskFormState);
  const [hydratedDataKey, setHydratedDataKey] = useState<string | undefined>();
  const [hasLocalChanges, setHasLocalChanges] = useState(false);

  const shouldFetch = !!campaignId && !!contractId && enabled;
  const { data: saleInformation, isPending: saleInformationPending } = useStaffGetSaleInformation(
    campaignId ?? '',
    contractId ?? '',
    shouldFetch
  );
  const { data: contractDates, isPending: contractDatesPending } = useStaffGetContractDates(
    campaignId ?? '',
    contractId ?? '',
    shouldFetch
  );
  const { data: contractDeposits, isPending: depositsPending } = useStaffGetContractDeposits(
    campaignId ?? '',
    contractId ?? '',
    shouldFetch
  );
  const { data: contractSettlement, isPending: settlementPending } = useStaffGetContractSettlement(
    campaignId ?? '',
    contractId ?? '',
    shouldFetch
  );
  const saleInformationFetching = useIsFetching({
    queryKey: ['staffGetSaleInformation', campaignId ?? '', contractId ?? ''],
  });
  const contractDatesFetching = useIsFetching({
    queryKey: ['staffGetContractDates', campaignId ?? '', contractId ?? ''],
  });
  const contractDepositsFetching = useIsFetching({
    queryKey: ['staffGetContractDeposits', campaignId ?? '', contractId ?? ''],
  });
  const contractSettlementFetching = useIsFetching({
    queryKey: ['staffGetContractSettlement', campaignId ?? '', contractId ?? ''],
  });
  const isFetchingInitialData =
    shouldFetch &&
    (saleInformationPending ||
      contractDatesPending ||
      depositsPending ||
      settlementPending ||
      saleInformationFetching > 0 ||
      contractDatesFetching > 0 ||
      contractDepositsFetching > 0 ||
      contractSettlementFetching > 0);

  const invalidateSaleInformation = useInvalidateUseStaffGetSaleInformation();
  const invalidateContractDates = useInvalidateUseStaffGetContractDates();
  const invalidateContractDeposits = useInvalidateUseStaffGetContractDeposits();
  const invalidateContractSettlement = useInvalidateUseStaffGetContractSettlement();
  const queryClient = useQueryClient();

  const { staffUpdateSaleInformation } = useStaffUpdateSaleInformation({
    onSuccess: () => {},
    onError: () => {},
  });
  const { staffUpdateContractDates } = useStaffUpdateContractDates({
    onSuccess: () => {},
    onError: () => {},
  });
  const { staffUpdateContractDeposits } = useStaffUpdateContractDeposits({
    onSuccess: () => {},
    onError: () => {},
  });
  const { staffUpdateContractSettlement } = useStaffUpdateContractSettlement({
    onSuccess: () => {},
    onError: () => {},
  });

  useEffect(() => {
    if (!shouldFetch) {
      setHydratedDataKey(undefined);
      setHasLocalChanges(false);
      setState(createInitialExchangeTaskFormState());
    }
  }, [shouldFetch]);

  useEffect(() => {
    const dataKey = JSON.stringify({
      contractDates,
      contractDeposits,
      contractId,
      contractSettlement,
      saleInformation,
    });

    if (!shouldFetch || isFetchingInitialData || hydratedDataKey === dataKey || hasLocalChanges)
      return;

    const initialDeposit = contractDeposits?.deposits?.find(
      (deposit) => deposit.depositType === DepositType.Initial
    );
    const balanceDeposit = contractDeposits?.deposits?.find(
      (deposit) => deposit.depositType === DepositType.Balance
    );
    const totalDepositPercent = (initialDeposit?.percent ?? 0) + (balanceDeposit?.percent ?? 0);
    const initialDepositAmount =
      initialDeposit?.amount ??
      (saleInformation?.salePrice && initialDeposit?.percent
        ? saleInformation.salePrice * (initialDeposit.percent / 100)
        : 0);
    const balanceDepositAmount =
      balanceDeposit?.amount ??
      (saleInformation?.salePrice && balanceDeposit?.percent
        ? saleInformation.salePrice * (balanceDeposit.percent / 100)
        : 0);
    const totalDepositAmount = initialDepositAmount + balanceDepositAmount;
    const hasDollarDeposit =
      initialDeposit?.depositValueType === ValueType.Dollar ||
      balanceDeposit?.depositValueType === ValueType.Dollar ||
      !!initialDeposit?.amount ||
      !!balanceDeposit?.amount;
    const deposit: Deposit | undefined =
      hasDollarDeposit && totalDepositAmount > 0
        ? {
            depositType: DepositType.Initial,
            depositValueType: ValueType.Dollar,
            amount: totalDepositAmount,
            isDepositSelected: true,
          }
        : totalDepositPercent === 10 || totalDepositPercent === 5
          ? {
              depositType: DepositType.Initial,
              depositValueType: ValueType.Percentage,
              percent: totalDepositPercent,
              isDepositSelected: true,
            }
          : undefined;

    setState({
      ...createInitialExchangeTaskFormState(),
      contractDate: dateOnlyToDate(contractDates?.contractDate) ?? undefined,
      coolingOffType: contractDates?.coolingOffDetails?.coolingOffType,
      customCoolingOffDays: contractDates?.coolingOffDetails?.coolingOffDays,
      noCoolingOffReason: contractDates?.coolingOffDetails?.noCoolingOffReason,
      salePrice: saleInformation?.salePrice ?? undefined,
      deposit,
      depositPayableOption:
        initialDeposit && !balanceDeposit ? 'full' : balanceDeposit ? 'quarter' : undefined,
      subjectToFinance: contractDates?.subjectToFinance,
      settlementInfo:
        contractSettlement?.settlementInfo ?? createInitialExchangeTaskFormState().settlementInfo,
    });
    setHydratedDataKey(dataKey);
    setHasLocalChanges(false);
  }, [
    contractDates,
    contractDeposits,
    contractSettlement,
    contractId,
    hasLocalChanges,
    hydratedDataKey,
    isFetchingInitialData,
    saleInformation,
    shouldFetch,
  ]);

  const updateState = (updates: Partial<ExchangeTaskFormState>) => {
    setHasLocalChanges(true);
    setState((previous) => ({
      ...previous,
      ...updates,
    }));
  };

  const saveTasks = async (taskTypes: (TaskCategoryTaskType | undefined)[]) => {
    if (!campaignId || !contractId) return;

    const savesInformation = taskTypes.some(isExchangeInformationTaskType);
    const savesContractDates = taskTypes.some(isExchangeContractDatesTaskType);
    const datesRequest = buildExchangeDatesRequest(state, contractDates);

    if (savesInformation) {
      const { saleRequest, depositsRequest } = buildExchangeInformationRequests(
        state,
        saleInformation
      );

      assertSuccessfulUpdate(
        await staffUpdateSaleInformation({
          campaignId,
          contractId,
          body: saleRequest,
        })
      );
      assertSuccessfulUpdate(
        await staffUpdateContractDeposits({
          contractId,
          body: depositsRequest,
        })
      );

      await invalidateSaleInformation(campaignId, contractId);
      await invalidateContractDeposits(campaignId, contractId);

      queryClient.setQueryData<StaffGetSaleInformationResponse>(
        ['staffGetSaleInformation', campaignId, contractId],
        {
          ...saleInformation,
          salePrice: saleRequest.salePrice,
          displaySalePrice: saleRequest.displaySalePrice,
          underOfferDisplayAs: saleInformation?.underOfferDisplayAs,
          unconditionalDisplayAs: saleInformation?.unconditionalDisplayAs,
        }
      );
      queryClient.setQueryData<StaffGetContractDepositsResponse>(
        ['staffGetContractDeposits', campaignId, contractId],
        {
          ...contractDeposits,
          deposits: depositsRequest.deposits ?? contractDeposits?.deposits,
        }
      );
    }

    if (savesInformation || savesContractDates) {
      assertSuccessfulUpdate(
        await staffUpdateContractDates({
          contractId,
          body: datesRequest,
        })
      );

      await invalidateContractDates(campaignId, contractId);
      queryClient.setQueryData<StaffGetContractDatesResponse>(
        ['staffGetContractDates', campaignId, contractId],
        {
          ...contractDates,
          ...datesRequest,
        }
      );
    }

    if (savesContractDates) {
      const settlementRequest: StaffUpdateContractSettlementRequest = {
        settlementInfo: buildSettlementInfo(state),
        signboardRemoval: contractSettlement?.signboardRemoval,
      };

      assertSuccessfulUpdate(
        await staffUpdateContractSettlement({
          campaignId,
          contractId,
          body: settlementRequest,
        })
      );

      await invalidateContractSettlement(campaignId, contractId);
      queryClient.setQueryData<StaffGetContractSettlementResponse>(
        ['staffGetContractSettlement', campaignId, contractId],
        {
          ...contractSettlement,
          settlementInfo: settlementRequest.settlementInfo,
          signboardRemoval: settlementRequest.signboardRemoval,
        }
      );
    }

    setHasLocalChanges(false);
  };

  return {
    isPending: isFetchingInitialData || (shouldFetch && hydratedDataKey === undefined),
    saveTasks,
    state,
    updateState,
  };
};
